import { EVERFLOW_AFFILIATE_PARAMETER_ENTRIES } from "./everflowParameters";
import type { EverflowAffiliateParams } from "./everflowParameters";

export type { EverflowAffiliateParams } from "./everflowParameters";

/**
 * Everflow affiliate params (offer, offer URL, affiliate, sub1-8) as captured
 * from a landing URL. Field names deliberately match
 * `EnrollmentItemTrackingMetadata` so values flow into
 * `enrollmentItem.metadata.tracking` without remapping.
 *
 * These campaign identifiers arrive via URL query params (`oid`, `uid`,
 * `affid`, `sub1`-`sub8`). Redirect transaction IDs also arrive on the landing
 * URL, but downstream code reads their captured first-party cookie rather
 * than query params; see getEverflowTransactionIdFromCookies.
 *
 * They are persisted to a first-party cookie on landing (see EverflowScript)
 * because the query string is gone after the first navigation, but leads are
 * usually created pages later (marketplace intake, program flow, clickout).
 *
 * Every affiliate-param field a landing can populate. The server-side
 * click-attribution key list (buildEverflowTracking) composes from this so
 * the two lists can't drift when a sub param is added.
 */
export const AFFILIATE_PARAM_FIELDS = EVERFLOW_AFFILIATE_PARAMETER_ENTRIES.map(
  ([, field]) => field
);

/**
 * The subset of AFFILIATE_PARAM_FIELDS that proves a landing carried real
 * Everflow *attribution* — the values we would otherwise back-fill from the
 * click log. Used to answer "did this capture already have attribution, or
 * should we go fetch it?".
 *
 * Deliberately EXCLUDES `everflowOfferUrlId`: a landing can carry `uid` alone
 * (it selects which offer URL was used) with no affiliate id and no subs.
 * Treating that as "already attributed" would suppress the click-log fill that
 * is the only thing populating the sub params for that lead — the exact case
 * this project exists to fix.
 */
export const AFFILIATE_ATTRIBUTION_FIELDS = AFFILIATE_PARAM_FIELDS.filter(
  (field) => field !== "everflowOfferUrlId"
);

/**
 * Parses Everflow affiliate params from a URL query string (`?oid=..&sub1=..`).
 * Returns null when no Everflow param is present so callers don't overwrite a
 * previously captured cookie with an empty object (last-touch: a new landing
 * WITH params replaces the whole set; a landing without params leaves the
 * previous capture intact).
 */
export function parseEverflowAffiliateParamsFromSearch(
  search: string
): EverflowAffiliateParams | null {
  const urlParams = new URLSearchParams(search);
  const params: EverflowAffiliateParams = {};
  for (const [urlKey, field] of EVERFLOW_AFFILIATE_PARAMETER_ENTRIES) {
    const value = urlParams.get(urlKey);
    if (value) params[field] = value;
  }
  return Object.keys(params).length > 0 ? params : null;
}

/**
 * Parses the affiliate-params cookie value (JSON written by EverflowScript).
 * Tolerates both URI-encoded (js-cookie's default write encoding) and raw
 * JSON, and drops unknown keys / non-string values so a tampered cookie can
 * never inject unexpected shapes downstream. Returns null on any parse
 * failure or when nothing valid remains.
 */
export function parseEverflowAffiliateParamsCookieValue(
  raw: string | undefined
): EverflowAffiliateParams | null {
  if (!raw) return null;
  for (const candidate of [raw, safeDecodeURIComponent(raw)]) {
    if (!candidate) continue;
    try {
      const parsed: unknown = JSON.parse(candidate);
      if (typeof parsed !== "object" || parsed === null) return null;
      const params: EverflowAffiliateParams = {};
      for (const field of AFFILIATE_PARAM_FIELDS) {
        const value = (parsed as Record<string, unknown>)[field];
        if (typeof value === "string" && value) params[field] = value;
      }
      return Object.keys(params).length > 0 ? params : null;
    } catch {
      // Fall through to the next decoding candidate.
    }
  }
  return null;
}

function safeDecodeURIComponent(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}
